'use client';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { V2Layout, RevealSection, useV2Lang, Eyebrow, Rule, Stat, L, DisplayTitle, Reveal, CountUp, formatDate, venueShort, ACCENT, HAIRLINE } from './_layout';
import { appLanguageToContentLocale } from '@/i18n/contentLocale';
import { eventHref } from './eventSlugs';

/**
 * Pathin — v2 home page (Sanity-inspired editorial preview).
 * Trilingual (CN / EN / DE).
 */

export type Slot = { start_time: string; end_time: string; location?: string; registration_link?: string; organizer?: string; support?: string; kind?: string };
export type Activity = {
  id: number; type: string; title: string; location: string; month?: number; slots: Slot[];
  display_date?: string | null; cover_url?: string | null;
  featured_rank?: number | null; show_in_highlights?: boolean; highlight_rank?: number | null;
  subtitle?: string | null; featured_lede?: string | null; excerpt?: string | null;
  venues?: string | null; partners?: string | null; format_label?: string | null;
};

export const COPY = {
  CN: {
    sections: { manifesto: '01 · 宣言', programs: '02 · 项目板块', featured: '03 · 巡演回顾', highlights: '04 · 精选项目', archive: '05 · 档案', partners: '06 · 合作伙伴', team: '07 · 团队' },
    hero: {
      eyebrow: '柏林 · 慕尼黑 · 北京 · 上海',
      lines: ['在中国与欧洲之间', '搭建文化桥梁'],
      lede: 'PathIN e.V. 是一家注册于慕尼黑的非营利机构，通过独立音乐与诗歌搭建文化桥梁。我们与艺术家在两种语言、两个大陆之间合作——一次一座舞台。',
      stats: { tours: '已策划巡演', cities: '到达城市', founded: '成立于' },
      foundedValue: '2025 年 4 月',
      scroll: '↓ 滚动',
    },
    manifesto: {
      headline: (
        <>
          我们让中国音乐人登上欧洲的舞台，让欧洲诗人走进中文读者的视野。让<em className="not-italic" style={{ color: ACCENT }}>远东</em>不再遥远——又不抹平它的棱角。
        </>
      ),
      body: (
        <>
          PathIN 于 2025 年 4 月在慕尼黑注册成立，是一家公益性的注册协会 (gemeinnütziger eingetragener Verein)，下设两条长期线。
          <span className="text-white"> GraviLens（引力透镜）</span>负责音乐板块——巡演规划、厂牌与场地合作、发行、跨城统筹；
          <span className="text-white"> Qiaodong（桥洞）</span> 负责翻译与诗歌——工作坊、节展策划、双语选集。
          一体两面。
        </>
      ),
    },
    programs: {
      title: '两条主线', lede: '同一个使命，两个完全不同的工作方式。',
      gravilens: { kicker: 'Music · 音乐', title: 'GraviLens 引力透镜', body: '欧洲与中国之间的独立音乐桥梁。我们做巡演策划、宣传、发行、跨境合作，支持独立音乐人将作品送到更远的地方。', cta: '进入 GraviLens →' },
      qiaodong: { kicker: 'Poetry · 诗歌', title: 'Qiaodong 桥洞', body: '一座让两种互相争辩的语言对话的桥。中德诗歌翻译工作坊、柏林诗歌节中国专场、双语选集《生活与洞见》正在进行中。', cta: '进入 桥洞 →' },
    },
    highlights: {
      title: '中欧之间。',
      lede: '已完成的合作。',
    },
    archive: { title: '完整事件档案', lede: '按时间倒序的全部活动。完整日程（含工作坊和长期项目）见', ctaInline: '事件页', loading: '加载中…', readMore: '阅读 ↗' },
    partners: { lede: '合作机构、共同主办、场地、节展、厂牌——以下是过往与当前在合作的伙伴。' },
    team: { lede: 'PathIN e.V. 的团队 — 4 个职能组。' },
    pull: { quote: '让远东不再遥远。\n让音乐打破偏见。', attr: '— GraviLens 引力透镜 · 宣言' },
  },
  EN: {
    sections: { manifesto: '01 · Manifesto', programs: '02 · Programs', featured: '03 · Tour Recap', highlights: '04 · Selected', archive: '05 · Archive', partners: '06 · Partners', team: '07 · Team' },
    hero: {
      eyebrow: 'Berlin · Munich · Beijing · Shanghai',
      lines: ['Cultural bridges between', 'China and Europe'],
      lede: 'PathIN e.V. is a Munich-based nonprofit building cultural bridges through independent music and poetry. We work with artists in two languages, two continents, one stage at a time.',
      stats: { tours: 'Tours produced', cities: 'Cities reached', founded: 'Founded' },
      foundedValue: 'April 2025',
      scroll: '↓ Scroll',
    },
    manifesto: {
      headline: (
        <>
          We send Chinese musicians to European stages. We bring European poets into Chinese readers' hands. We make the <em className="not-italic" style={{ color: ACCENT }}>far East</em> feel less far — without smoothing it down.
        </>
      ),
      body: (
        <>
          Founded in Munich in April 2025 as a registered nonprofit (gemeinnütziger eingetragener Verein), PathIN runs two long-form programs.
          <span className="text-white"> GraviLens（引力透镜）</span> handles the music — tour planning, label and venue partnerships, releases, multi-city logistics.
          <span className="text-white"> Qiaodong（桥洞）</span> handles translation and poetry — workshops, festival programming, anthologies. One mission, two angles.
        </>
      ),
    },
    programs: {
      title: 'Two parallel lines', lede: 'Same mission, two completely different working methods.',
      gravilens: { kicker: 'Music', title: 'GraviLens 引力透镜', body: 'An independent music bridge between Europe and China. Tour planning, promotion, releases and cross-border collaboration — supporting indie artists in carrying their work further.', cta: 'Enter GraviLens →' },
      qiaodong: { kicker: 'Poetry', title: 'Qiaodong 桥洞', body: 'A bridge between two languages that argue with each other. Chinese-German translation workshops, the China program at Berlin Poetry Festival, and the bilingual anthology 《生活与洞见》 — in motion.', cta: 'Enter Qiaodong →' },
    },
    highlights: {
      title: 'Between China and Europe.',
      lede: 'Completed collaborations.',
    },
    archive: { title: 'Selected past programming', lede: 'Chronological view. For the full schedule including upcoming poetry workshops, see the', ctaInline: 'events page', loading: 'Loading archive…', readMore: 'Read ↗' },
    partners: { lede: "Co-organizers, venues, festivals, labels and institutions we've worked with on the past or current cycle." },
    team: { lede: 'The people behind PathIN e.V. — four functional teams.' },
    pull: { quote: 'Make the far East feel less far.\nLet music break prejudice.', attr: '— GraviLens 引力透镜 · Manifesto' },
  },
  DE: {
    sections: { manifesto: '01 · Manifest', programs: '02 · Programme', featured: '03 · Tour-Rückblick', highlights: '04 · Auswahl', archive: '05 · Archiv', partners: '06 · Partner', team: '07 · Team' },
    hero: {
      eyebrow: 'Berlin · München · Peking · Shanghai',
      lines: ['Kulturbrücken zwischen', 'China und Europa'],
      lede: 'PathIN e.V. ist ein in München eingetragener gemeinnütziger Verein, der durch unabhängige Musik und Poesie kulturelle Brücken baut. Wir arbeiten mit Künstler:innen in zwei Sprachen und auf zwei Kontinenten — eine Bühne nach der anderen.',
      stats: { tours: 'Tourneen produziert', cities: 'Städte erreicht', founded: 'Gegründet' },
      foundedValue: 'April 2025',
      scroll: '↓ Scrollen',
    },
    manifesto: {
      headline: (
        <>
          Wir bringen chinesische Musiker:innen auf europäische Bühnen. Wir bringen europäische Lyrik in chinesische Hände. Wir lassen den <em className="not-italic" style={{ color: ACCENT }}>Fernen Osten</em> näher rücken — ohne ihn zu glätten.
        </>
      ),
      body: (
        <>
          Im April 2025 in München als gemeinnütziger eingetragener Verein gegründet, betreibt PathIN zwei langfristige Programme.
          <span className="text-white"> GraviLens（引力透镜）</span> kümmert sich um Musik — Tourneeplanung, Label- und Venue-Partnerschaften, Releases, Logistik über mehrere Städte.
          <span className="text-white"> Qiaodong（桥洞）</span> kümmert sich um Übersetzung und Poesie — Workshops, Festival-Programmierung, Anthologien. Eine Mission, zwei Perspektiven.
        </>
      ),
    },
    programs: {
      title: 'Zwei parallele Linien', lede: 'Gleiche Mission, zwei sehr unterschiedliche Arbeitsweisen.',
      gravilens: { kicker: 'Musik', title: 'GraviLens 引力透镜', body: 'Eine Brücke für unabhängige Musik zwischen Europa und China. Tourplanung, Promotion, Releases und grenzübergreifende Zusammenarbeit — wir helfen unabhängigen Künstler:innen, weiter zu kommen.', cta: 'Zu GraviLens →' },
      qiaodong: { kicker: 'Poesie', title: 'Qiaodong 桥洞', body: 'Eine Brücke zwischen zwei Sprachen, die miteinander streiten. Chinesisch-deutsche Übersetzungs-Workshops, China-Programm beim Poesiefestival Berlin, zweisprachige Anthologie 《生活与洞见》 — in Bewegung.', cta: 'Zu Qiaodong →' },
    },
    highlights: {
      title: 'Zwischen China und Europa.',
      lede: 'Abgeschlossene Kollaborationen.',
    },
    archive: { title: 'Ausgewählte vergangene Programme', lede: 'Chronologische Ansicht. Den vollständigen Spielplan inkl. kommender Workshops gibt es auf der', ctaInline: 'Events-Seite', loading: 'Archiv wird geladen…', readMore: 'Lesen ↗' },
    partners: { lede: 'Co-Veranstalter, Spielstätten, Festivals, Labels und Institutionen aus früheren und laufenden Zyklen.' },
    team: { lede: 'Die Menschen hinter PathIN e.V. — vier Funktionsteams.' },
    pull: { quote: 'Den Fernen Osten näher rücken.\nMusik gegen Vorurteile.', attr: '— GraviLens 引力透镜 · Manifest' },
  },
} as const;

type PartnerCity = { CN: string; EN: string; DE: string };
export const PARTNERS: Array<{ name: string; city: PartnerCity }> = [
  { name: 'Dunk! Festival',                city: { CN: '比利时',     EN: 'Belgium', DE: 'Belgien' } },
  { name: 'Haus für Poesie',               city: { CN: '柏林',       EN: 'Berlin',  DE: 'Berlin'  } },
  { name: 'Akademie der Künste',           city: { CN: '柏林',       EN: 'Berlin',  DE: 'Berlin'  } },
  { name: 'Goethe-Institut',               city: { CN: '—',          EN: '—',       DE: '—'       } },
  { name: 'Konfuzius-Institut Ingolstadt', city: { CN: '巴伐利亚',   EN: 'Bavaria', DE: 'Bayern'  } },
  { name: 'Nugget Records',                city: { CN: '柏林',       EN: 'Berlin',  DE: 'Berlin'  } },
  { name: '1724 Records',                  city: { CN: '中国',       EN: 'China',   DE: 'China'   } },
  { name: 'Positive Transmitter',          city: { CN: '柏林',       EN: 'Berlin',  DE: 'Berlin'  } },
  { name: 'Brainbowl Records',             city: { CN: '柏林',       EN: 'Berlin',  DE: 'Berlin'  } },
  { name: 'Sheddog Booking',               city: { CN: '柏林',       EN: 'Berlin',  DE: 'Berlin'  } },
  { name: '5Taste',                        city: { CN: '汉堡',       EN: 'Hamburg', DE: 'Hamburg' } },
  { name: 'Andreas / Noise Raid',          city: { CN: '慕尼黑',     EN: 'Munich',  DE: 'München' } },
];

type TeamMember = { name: string; role?: { CN: string; EN: string; DE: string } };
type TeamGroup = { name: { CN: string; EN: string; DE: string }; members: TeamMember[] };

export const TEAMS: TeamGroup[] = [
  {
    name: { CN: '理事会 Vorstand', EN: 'Vorstand · Board', DE: 'Vorstand' },
    members: [
      { name: '黄铭 · Ming Huang', role: { CN: '会长', EN: 'President', DE: 'Vorsitzender' } },
      { name: '何柏岚 · Bailan He', role: { CN: '副会长', EN: 'Vice President', DE: 'Vize-Vorsitzender' } },
      { name: '马可 · Ke Ma', role: { CN: '秘书 · 财务', EN: 'Secretary · Finance', DE: 'Schriftführer · Finanzen' } },
    ],
  },
  {
    name: { CN: 'GraviLens 引力透镜', EN: 'GraviLens 引力透镜', DE: 'GraviLens 引力透镜' },
    members: [
      { name: '韩冰意 · Bingyi Han', role: { CN: '艺人关系', EN: 'Artist Relations', DE: 'Künstler:innen-Relations' } },
      { name: '杨润荃 · David Runquan Yang', role: { CN: '巡演资源', EN: 'Tour Resources', DE: 'Tour-Ressourcen' } },
      { name: '谢沛文 · Peiwen Xie', role: { CN: '场地 · 现场', EN: 'Venues · Production', DE: 'Venues · Produktion' } },
      { name: '高睿 · Tony Rui Gao', role: { CN: '现场 · 巡演执行', EN: 'Production · Tour Manager', DE: 'Produktion · Tour-Management' } },
      { name: '张子琼 · Joe Ziqiong Zhang', role: { CN: '财务 · 票务 · 市场', EN: 'Finance · Ticketing · Marketing', DE: 'Finanzen · Ticketing · Marketing' } },
    ],
  },
  {
    name: { CN: 'Qiaodong 桥洞', EN: 'Qiaodong 桥洞', DE: 'Qiaodong 桥洞' },
    members: [
      { name: '孙汤明米 · Tangmi', role: { CN: '项目主理', EN: 'Program Lead', DE: 'Programmleitung' } },
    ],
  },
  {
    name: { CN: '内容与传播', EN: 'Content & Comms', DE: 'Content & Kommunikation' },
    members: [
      { name: '芮雨田 · Yutian Rui', role: { CN: '视觉 · 后期 · 中文社媒', EN: 'Visual · Post · CN Social', DE: 'Visuell · Post · CN-Social' } },
      { name: '陈希 · Xi Chen', role: { CN: '海外社媒', EN: 'International Social', DE: 'Internationale Social-Media' } },
      { name: '杜瑞珈 · Ruijia Du', role: { CN: '网站开发', EN: 'Web Development', DE: 'Web-Entwicklung' } },
    ],
  },
  {
    name: { CN: '顾问 · 地区联络', EN: 'Advisors · Regional Liaison', DE: 'Beirat · Regionale Verbindung' },
    members: [
      { name: '叶弎 · San Ye', role: { CN: '柏林地区联系人', EN: 'Berlin Regional Contact', DE: 'Berliner Regionalkontakt' } },
    ],
  },
];

// 03/04 状态相关文案（按语言）
export const FEAT_TXT = {
  CN: { active: '03 · 正在售票', recap: '03 · 巡演回顾', ctaActive: '↗ 查看完整行程', ctaRecap: '↗ 查看巡演回顾', cardCta: '阅读详情 →', done: '已完成', ongoing: '进行中', stops: '站巡演', invited: '受邀 ' },
  EN: { active: '03 · On Sale', recap: '03 · Tour Recap', ctaActive: '↗ View tour', ctaRecap: '↗ Read the recap', cardCta: 'Read full case →', done: 'completed', ongoing: 'ongoing', stops: '-city tour', invited: 'invited to ' },
  DE: { active: '03 · Im Vorverkauf', recap: '03 · Tour-Rückblick', ctaActive: '↗ Tour ansehen', ctaRecap: '↗ Rückblick lesen', cardCta: 'Vollständige Fallstudie →', done: 'abgeschlossen', ongoing: 'laufend', stops: '-Städte-Tour', invited: 'eingeladen zum ' },
} as const;

// 04 卡片美术编排（按 activity id；无配置项 = 纯文字卡）
export const HL_VISUALS: Record<number, { imgSrc?: string; imgAlt?: string; imgPos?: 'left' | 'right'; imgFit?: 'cover' | 'contain'; textOnly?: boolean }> = {
  6: { imgSrc: '/images/events/oys-berlin-2026/photo-JM502484.jpg', imgAlt: 'Omnipotent Youth Society · Kasselhaus Berlin', imgPos: 'left' },
  2: { textOnly: true },
  4: { imgSrc: '/images/highlights/an-corporation.jpg', imgAlt: 'An Corporation', imgPos: 'right', imgFit: 'contain' },
  3: { imgSrc: '/images/highlights/fazi.jpg', imgAlt: 'FAZI', imgPos: 'left' },
};

export const toMs = (s?: string) => (s ? new Date(s.replace(' ', 'T')).getTime() : 0);
export function splitVenueCity(loc: string) {
  const parts = loc.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) return { venue: parts[0] ?? '', city: '' };
  return { venue: parts[0], city: parts[parts.length - 2].replace(/^\d{4,6}\s+/, '') };
}

export default function V2Home() {
  const lang = useV2Lang();
  const t = COPY[lang];
  const ftx = FEAT_TXT[lang];
  const [events, setEvents] = useState<Activity[]>([]);

  useEffect(() => {
    const apiLang = appLanguageToContentLocale(lang);
    fetch(`/api/activities?language=${apiLang}&pageSize=50`)
      .then((r) => r.json())
      .then((data) => setEvents(Array.isArray(data) ? data : data?.activities ?? data?.data ?? []))
      .catch(() => setEvents([]));
  }, [lang]);

  const sorted = [...events]
    .filter((e) => e.slots && e.slots.length > 0)
    .sort((a, b) => new Date(b.slots[0].start_time.replace(' ', 'T')).getTime() - new Date(a.slots[0].start_time.replace(' ', 'T')).getTime());

  // 03 巡演 hero：取 featured_rank 最小者；状态按 slot 日期自动派生
  const nowMs = Date.now();
  const featured = [...events].filter((e) => e.featured_rank != null).sort((a, b) => a.featured_rank! - b.featured_rank!)[0];
  const featuredState: 'active' | 'recap' | null = !featured ? null
    : Math.max(...featured.slots.map((s) => toMs(s.end_time))) >= nowMs ? 'active' : 'recap';
  const fSlots = featured?.slots ?? [];
  const fStart = fSlots[0] ? formatDate(fSlots[0].start_time, lang) : null;
  const fEnd = fSlots.length ? formatDate(fSlots[fSlots.length - 1].start_time, lang) : null;
  const featWindow = fStart && fEnd ? `${fStart.month} ${fStart.day} — ${fEnd.month} ${fEnd.day}` : '';
  const tourStops = fSlots.filter((s) => s.kind !== 'festival');
  const festNames = fSlots.filter((s) => s.kind === 'festival').map((s) => splitVenueCity(s.location ?? '').venue).filter(Boolean).join(' · ');
  const tourPart = lang === 'CN' ? `${tourStops.length} ${ftx.stops}` : `${tourStops.length}${ftx.stops}`;
  const featMeta = festNames ? `${tourPart} · ${ftx.invited}${festNames}` : tourPart;

  // 04 精选：钉住（highlight_rank）+ 自动补位（最近优先），上限 5
  const hlRecency = (e: Activity) => toMs(e.display_date ?? e.slots[0]?.start_time);
  const hlPinned = events.filter((e) => e.highlight_rank != null).sort((a, b) => a.highlight_rank! - b.highlight_rank!);
  const hlAuto = events.filter((e) => e.show_in_highlights && e.highlight_rank == null).sort((a, b) => hlRecency(b) - hlRecency(a));
  const highlights = [...hlPinned, ...hlAuto].slice(0, 5);

  return (
    <V2Layout>
      {/* HERO */}
      <HeroSection t={t} lang={lang} />

      <Rule />

      {/* MANIFESTO */}
      <RevealSection id="manifesto" className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow>{t.sections.manifesto}</Eyebrow></div>
          <div className="md:col-span-8">
            <p className="v2-cjk-tight text-[clamp(28px,3.2vw,48px)] leading-[1.22] tracking-[-0.025em] font-medium text-white">{t.manifesto.headline}</p>
            <p className="mt-10 text-[16px] leading-[1.75] text-white/80 max-w-[64ch]">{t.manifesto.body}</p>
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* PROGRAMS */}
      <RevealSection id="programs" className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid gap-12 md:grid-cols-12 mb-16">
            <div className="md:col-span-3"><Eyebrow>{t.sections.programs}</Eyebrow></div>
            <div className="md:col-span-8">
              <h3 className="v2-cjk-tight text-[clamp(28px,3vw,44px)] leading-[1.15] tracking-[-0.025em] font-bold">{t.programs.title}</h3>
              <p className="mt-4 text-[16px] leading-[1.7] text-white/75 max-w-[60ch]">{t.programs.lede}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 border-t" style={{ borderColor: HAIRLINE }}>
            <Link to="/GraviLens" className="group block py-12 md:py-20 md:pr-12 border-b md:border-b-0 md:border-r" style={{ borderColor: HAIRLINE }}>
              <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/55 group-hover:text-white transition-colors font-medium">{t.programs.gravilens.kicker}</span>
              <h4 className="v2-cjk-tight mt-4 text-[clamp(40px,5.5vw,80px)] leading-[0.95] tracking-[-0.035em] font-black text-white" style={{ wordBreak: 'keep-all' }}>{t.programs.gravilens.title}</h4>
              <p className="mt-6 text-[16px] leading-[1.7] text-white/80 max-w-[40ch]">{t.programs.gravilens.body}</p>
              <span className="inline-block mt-8 text-[14px] border-b-4 pb-2 font-semibold tracking-wide transition-all group-hover:translate-x-1" style={{ borderColor: ACCENT, color: ACCENT }}>{t.programs.gravilens.cta}</span>
            </Link>
            <Link to="/Qiaodong" className="group block py-12 md:py-20 md:pl-12">
              <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/55 group-hover:text-white transition-colors font-medium">{t.programs.qiaodong.kicker}</span>
              <h4 className="v2-cjk-tight mt-4 text-[clamp(40px,5.5vw,80px)] leading-[0.95] tracking-[-0.035em] font-black text-white" style={{ wordBreak: 'keep-all' }}>{t.programs.qiaodong.title}</h4>
              <p className="mt-6 text-[16px] leading-[1.7] text-white/80 max-w-[40ch]">{t.programs.qiaodong.body}</p>
              <span className="inline-block mt-8 text-[14px] border-b-4 pb-2 font-semibold tracking-wide transition-all group-hover:translate-x-1" style={{ borderColor: ACCENT, color: ACCENT }}>{t.programs.qiaodong.cta}</span>
            </Link>
          </div>
        </div>
      </RevealSection>

      {/* FEATURED — 03 巡演 hero（数据驱动；无 featured 时整块隐藏） */}
      {featured && (
        <>
          <Rule />
          <RevealSection id="featured" className="px-6 md:px-10 py-24 md:py-32">
            <div className="mx-auto max-w-[1400px]">
              <div className="grid gap-12 md:grid-cols-12">
                <div className="md:col-span-3">
                  <Eyebrow accent>{featuredState === 'active' ? ftx.active : ftx.recap}</Eyebrow>
                  {featured.featured_lede && <p className="mt-4 text-[13px] leading-[1.55] text-white/75">{featured.featured_lede}</p>}
                </div>
                <div className="md:col-span-9">
                  <Link to={`/events/${featured.id}`} className="group block">
                    {featured.cover_url && (
                      <div className="relative overflow-hidden rounded-sm">
                        <img src={featured.cover_url} alt={featured.title} className="w-full h-auto aspect-[16/7] object-cover transition-transform duration-700 group-hover:scale-[1.02]" />
                      </div>
                    )}
                    <div className="mt-8 grid gap-x-12 gap-y-3 md:grid-cols-12">
                      <h2 className="v2-cjk-tight md:col-span-9 text-[clamp(36px,5vw,72px)] leading-[1.0] tracking-[-0.035em] font-black">
                        {featured.title}
                        {featured.subtitle && <span className="block text-white/70 italic mt-2 text-[clamp(18px,1.6vw,26px)] tracking-[-0.015em] font-normal">{featured.subtitle}</span>}
                      </h2>
                      <div className="md:col-span-3 flex flex-col gap-1 text-[13px] tracking-wide text-white/80 self-end font-mono">
                        <span className="text-white">{featWindow}</span>
                        <span>{featMeta}</span>
                        <span style={{ color: ACCENT }}>{featuredState === 'active' ? ftx.ctaActive : ftx.ctaRecap}</span>
                      </div>
                    </div>
                  </Link>
                  <div className="mt-12 border-t" style={{ borderColor: HAIRLINE }}>
                    {featured.slots.map((slot, i) => {
                      const fd = formatDate(slot.start_time, lang);
                      const { venue, city } = splitVenueCity(slot.location ?? '');
                      return (
                        <div key={i} className="grid grid-cols-12 gap-4 py-5 border-b group hover:bg-white/[0.02] transition-colors items-baseline" style={{ borderColor: HAIRLINE }}>
                          <span className="col-span-3 md:col-span-2 font-mono text-[13px] tracking-[0.05em] text-white font-medium">{fd.month} {fd.day}</span>
                          <span className="col-span-9 md:col-span-3 text-[15px] text-white">{city}</span>
                          <span className="col-span-12 md:col-span-3 text-[15px] text-white/80">{venue}</span>
                          <span className="col-span-12 md:col-span-4 text-[13px] text-white/65">{slot.support}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </RevealSection>
        </>
      )}

      <Rule />

      {/* HIGHLIGHTS */}
      <RevealSection id="highlights" className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid gap-12 md:grid-cols-12 mb-16">
            <div className="md:col-span-3"><Eyebrow>{t.sections.highlights}</Eyebrow></div>
            <div className="md:col-span-8">
              <h3 className="v2-cjk-tight text-[clamp(28px,3.2vw,48px)] leading-[1.22] tracking-[-0.025em] font-medium text-white">{t.highlights.title}</h3>
              <p className="mt-6 text-[16px] leading-[1.7] text-white/75 max-w-[60ch]">{t.highlights.lede}</p>
            </div>
          </div>
          {highlights.map((ev, i) => {
            const v = HL_VISUALS[ev.id] ?? { textOnly: true };
            const fd = formatDate(ev.display_date ?? ev.slots[0]?.start_time ?? '', lang);
            const isPast = ev.slots.length > 0 && Math.max(...ev.slots.map((s) => toMs(s.end_time))) < nowMs;
            const statusWord = isPast ? ftx.done : ftx.ongoing;
            const tag = ev.format_label ? `${ev.format_label} · ${statusWord}` : statusWord;
            return (
              <HighlightCard
                key={ev.id}
                tag={tag}
                title={ev.title}
                date={`${fd.year} · ${fd.month} ${fd.day}`}
                body={ev.excerpt ?? ''}
                venues={ev.venues ?? ''}
                partners={ev.partners ?? ''}
                cta={ftx.cardCta}
                href={eventHref(ev.id)}
                imgPos={v.imgPos}
                imgSrc={v.imgSrc}
                imgAlt={v.imgAlt}
                imgFit={v.imgFit}
                textOnly={v.textOnly}
                isLast={i === highlights.length - 1}
              />
            );
          })}
        </div>
      </RevealSection>

      <Rule />

      {/* ARCHIVE */}
      <RevealSection id="archive" className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid gap-12 md:grid-cols-12 mb-16">
            <div className="md:col-span-3"><Eyebrow>{t.sections.archive}</Eyebrow></div>
            <div className="md:col-span-8">
              <h3 className="v2-cjk-tight text-[clamp(28px,3vw,44px)] leading-[1.15] tracking-[-0.025em] font-bold">{t.archive.title}</h3>
              <p className="mt-4 text-[16px] leading-[1.7] text-white/75 max-w-[60ch]">
                {t.archive.lede}{' '}
                <Link to="/events" className="text-white underline underline-offset-4 hover:no-underline font-normal">{t.archive.ctaInline}</Link>.
              </p>
            </div>
          </div>
          <div className="border-t" style={{ borderColor: HAIRLINE }}>
            {sorted.length === 0 && <div className="py-16 text-center text-white/55 text-sm">{t.archive.loading}</div>}
            {sorted.map((ev) => {
              const f = formatDate(ev.slots[0].start_time, lang);
              return (
                <Link key={ev.id} to={`/events/${ev.id}`} className="grid grid-cols-12 gap-4 py-7 md:py-9 border-b items-start group hover:bg-white/[0.02] transition-colors px-1" style={{ borderColor: HAIRLINE }}>
                  <span className="col-span-3 md:col-span-1 font-mono text-[12px] tracking-[0.1em] text-white/65 mt-2 font-medium">{f.year}</span>
                  <span className="col-span-9 md:col-span-2 font-mono text-[13px] uppercase tracking-[0.1em] text-white mt-2 font-medium">{f.month} {f.day}</span>
                  <h4 className="v2-cjk-tight col-span-12 md:col-span-6 text-[clamp(20px,1.8vw,28px)] leading-[1.25] tracking-[-0.015em] font-semibold text-white">{ev.title}</h4>
                  <span className="col-span-7 md:col-span-2 text-[13px] text-white/70 mt-2">{venueShort(ev.slots[0].location ?? ev.location)}</span>
                  <span className="col-span-5 md:col-span-1 text-right text-[12px] font-mono uppercase tracking-wider text-white/65 mt-2">{t.archive.readMore}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* PARTNERS */}
      <RevealSection id="partners" className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3">
            <Eyebrow>{t.sections.partners}</Eyebrow>
            <p className="mt-4 text-[13px] leading-[1.55] text-white/65 max-w-[28ch]">{t.partners.lede}</p>
          </div>
          <div className="md:col-span-9">
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1 border-t pt-2" style={{ borderColor: HAIRLINE }}>
              {PARTNERS.map(({ name, city }) => (
                <li key={name} className="flex items-baseline justify-between border-b py-4 text-[15px]" style={{ borderColor: HAIRLINE }}>
                  <span className="text-white">{name}</span>
                  <span className="font-mono text-[11px] tracking-wider text-white/55 uppercase">{city[lang]}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* TEAM */}
      <RevealSection id="team" className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3">
            <Eyebrow>{t.sections.team}</Eyebrow>
            <p className="mt-4 text-[13px] leading-[1.55] text-white/65 max-w-[28ch]">{t.team.lede}</p>
          </div>
          <div className="md:col-span-9">
            {TEAMS.map((team) => (
              <div key={team.name.EN} className="mb-12 last:mb-0">
                <h3 className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/55 font-medium pb-3 border-b" style={{ borderColor: HAIRLINE }}>
                  {team.name[lang]}
                </h3>
                <ul>
                  {team.members.map((m) => (
                    <li key={m.name} className="grid grid-cols-12 gap-4 border-b py-5 items-baseline" style={{ borderColor: HAIRLINE }}>
                      <span className="col-span-12 md:col-span-7 text-[clamp(18px,1.5vw,22px)] tracking-tight text-white">{m.name}</span>
                      {m.role && (
                        <span className="col-span-12 md:col-span-5 text-[13px] text-white/70">{m.role[lang]}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* PULL QUOTE
          Each sentence on its own line (forced via \n in the COPY string) so
          there's no orphan-character at the bottom. The opening " is INLINE
          inside the first line's span — not a separate block — so it sits
          on the same baseline as the first character of text. */}
      <RevealSection className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[900px] text-center">
          <p
            className="v2-serif v2-cjk-tight text-[clamp(28px,3.4vw,48px)] leading-[1.5] tracking-[-0.005em] text-white mx-auto"
            style={{ fontStyle: 'italic', fontWeight: 400 }}
          >
            {(() => {
              const lines = t.pull.quote.split('\n');
              return lines.map((raw, i) => {
                const isFirst = i === 0;
                const isLast = i === lines.length - 1;
                const m = raw.match(/[。.?!？！]+$/);
                const trailing = m?.[0] ?? '';
                const body = trailing ? raw.slice(0, -trailing.length) : raw;
                const punct = trailing
                  .replace(/。/g, '.')
                  .replace(/？/g, '?')
                  .replace(/！/g, '!');
                return (
                  <span key={i} className="block">
                    {isFirst && (
                      <span
                        aria-hidden
                        style={{
                          color: ACCENT,
                          fontSize: '1.5em',
                          lineHeight: 0,
                          verticalAlign: '-0.18em',
                          marginRight: '0.04em',
                        }}
                      >
                        &ldquo;
                      </span>
                    )}
                    {body}
                    {punct &&
                      (isLast ? (
                        <span lang="en" style={{ color: ACCENT }}>{punct}</span>
                      ) : (
                        <span lang="en">{punct}</span>
                      ))}
                  </span>
                );
              });
            })()}
          </p>
          <p className="mt-10 font-mono text-[12px] uppercase tracking-[0.22em] text-white/65 font-medium">
            {t.pull.attr}
          </p>
        </div>
      </RevealSection>

      <Rule />
    </V2Layout>
  );
}

/* ──────── HeroSection (video bg + editorial overlay) ──────── */

function HeroSection({ t, lang }: { t: typeof COPY[keyof typeof COPY]; lang: 'CN' | 'EN' | 'DE' }) {
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return (
    <RevealSection className="relative min-h-[100vh] flex items-center px-6 md:px-10 pb-24 pt-32 overflow-hidden bg-[#0c0b0a]">
      {/* Video background */}
      {!reduceMotion && (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/images/highlights/fazi.jpg"
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-55"
        >
          <source src="/band.mp4" type="video/mp4" />
        </video>
      )}
      {/* Overlay — keeps text readable on top of any frame */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(12,11,10,0.50) 0%, rgba(12,11,10,0.30) 40%, rgba(12,11,10,0.85) 100%)',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'rgba(12,11,10,0.15)' }}
      />

      <div className="relative z-10 mx-auto max-w-[1400px] w-full">
        <Eyebrow accent>{t.hero.eyebrow}</Eyebrow>
        <h1 className="v2-cjk-tight mt-6 text-[clamp(48px,9vw,148px)] leading-[1.0] tracking-[-0.04em] font-black">
          {t.hero.lines.map((line, i) => {
            const isLast = i === t.hero.lines.length - 1;
            return (
              <span key={i} className="block">
                {line}
                {isLast && (
                  <span lang="en" style={{ color: ACCENT }}>.</span>
                )}
              </span>
            );
          })}
        </h1>
        <div className="mt-10 grid gap-12 md:grid-cols-12">
          <p className="md:col-span-6 md:col-start-1 text-[clamp(16px,1.35vw,20px)] leading-[1.7] text-white/90 max-w-prose">
            {t.hero.lede}
          </p>
          <div className="md:col-span-3 md:col-start-10 flex flex-col gap-2 text-[13px] tracking-wide text-white/85">
            <Stat label={t.hero.stats.tours} value="5" />
            <Stat label={t.hero.stats.cities} value="11+" />
            <Stat label={t.hero.stats.founded} value={t.hero.foundedValue} />
          </div>
        </div>
      </div>
      <div className="absolute right-6 top-24 hidden md:flex flex-col items-end gap-2 text-[11px] tracking-[0.22em] text-white/75 uppercase z-10 font-medium">
        <span>{t.hero.scroll}</span>
        <span><L>Issue 01</L></span>
      </div>
    </RevealSection>
  );
}

/* ──────── HighlightCard (used on home only) ──────── */

function HighlightCard({
  tag, title, date, body, venues, partners, cta, href,
  imgPos, imgSrc, imgAlt, imgFit = 'cover', textOnly, isLast,
}: {
  tag: string; title: string; date: string; body: string; venues: string; partners: string; cta: string;
  href: string;
  imgPos?: 'left' | 'right'; imgSrc?: string; imgAlt?: string;
  imgFit?: 'cover' | 'contain';
  textOnly?: boolean;
  isLast?: boolean;
}) {
  const lang = useV2Lang();
  const labels = lang === 'CN' ? { venue: '场地', partners: '合作' }
               : lang === 'DE' ? { venue: 'ORT', partners: 'PARTNER' }
               : { venue: 'VENUE', partners: 'PARTNERS' };
  if (textOnly) {
    return (
      <Link
        to={href}
        className={`group block py-16 md:py-24 ${isLast ? '' : 'border-b'}`}
        style={{ borderColor: HAIRLINE }}
      >
        <div className="grid gap-8 md:grid-cols-12 md:gap-12 items-start">
          <div className="md:col-span-3">
            <div className="flex flex-col gap-2">
              <span className="inline-block h-[1px] w-6" style={{ backgroundColor: ACCENT }} aria-hidden />
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase" style={{ color: ACCENT }}>{tag}</span>
              <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/55 mt-1">{date}</span>
            </div>
          </div>
          <div className="md:col-span-9">
            <h3 className="v2-cjk-tight text-[clamp(32px,4.4vw,68px)] leading-[1.05] tracking-[-0.035em] font-black max-w-[20ch]">{title}</h3>
            <p className="mt-8 text-[clamp(17px,1.4vw,21px)] leading-[1.65] text-white/75 max-w-[55ch]">{body}</p>
            <div className="mt-10 flex flex-col gap-1.5 text-[13px] font-mono tracking-wide text-white/75">
              <span><span className="text-white/55 inline-block w-20">{labels.venue}</span> {venues}</span>
              <span><span className="text-white/55 inline-block w-20">{labels.partners}</span> {partners}</span>
            </div>
            <span className="inline-block mt-8 text-[14px] border-b-4 pb-2 font-semibold tracking-wide transition-all group-hover:translate-x-1" style={{ borderColor: ACCENT, color: ACCENT }}>{cta}</span>
          </div>
        </div>
      </Link>
    );
  }
  return (
    <Link to={href} className={`group block py-12 md:py-20 ${isLast ? '' : 'border-b'}`} style={{ borderColor: HAIRLINE }}>
      <div className="grid gap-8 md:grid-cols-12 md:gap-12 items-start">
        {imgPos === 'left' && imgSrc && (
          <div className="md:col-span-5 md:order-1 overflow-hidden">
            <img src={imgSrc} alt={imgAlt} className={`w-full h-auto aspect-[4/3] ${imgFit === 'contain' ? 'object-contain' : 'object-cover'} transition-transform duration-700 group-hover:scale-[1.03]`} />
          </div>
        )}
        <div className={`md:col-span-7 ${imgPos === 'left' ? 'md:order-2' : 'md:order-1'}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-block h-[1px] w-6" style={{ backgroundColor: ACCENT }} aria-hidden />
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase" style={{ color: ACCENT }}>{tag}</span>
            <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/55">· {date}</span>
          </div>
          <h3 className="v2-cjk-tight text-[clamp(28px,3.4vw,52px)] leading-[1.1] tracking-[-0.035em] font-extrabold">{title}</h3>
          <p className="mt-6 text-[16px] leading-[1.7] text-white/70 max-w-[60ch]">{body}</p>
          <div className="mt-8 flex flex-col gap-1.5 text-[13px] font-mono tracking-wide text-white/75">
            <span><span className="text-white/55 inline-block w-20">{labels.venue}</span> {venues}</span>
            <span><span className="text-white/55 inline-block w-20">{labels.partners}</span> {partners}</span>
          </div>
          <span className="inline-block mt-8 text-[14px] border-b-4 pb-2 font-semibold tracking-wide transition-all group-hover:translate-x-1" style={{ borderColor: ACCENT, color: ACCENT }}>{cta}</span>
        </div>
        {imgPos === 'right' && imgSrc && (
          <div className="md:col-span-5 md:order-2 overflow-hidden">
            <img src={imgSrc} alt={imgAlt} className={`w-full h-auto aspect-[4/3] ${imgFit === 'contain' ? 'object-contain' : 'object-cover'} transition-transform duration-700 group-hover:scale-[1.03]`} />
          </div>
        )}
      </div>
    </Link>
  );
}
